// Central offer-sharing rules prevent duplicate links across Web Share, WhatsApp, and copy actions.
const getDefaultOrigin = () => (
  typeof window !== 'undefined' && window.location?.origin
    ? window.location.origin
    : 'https://miskest.com'
);

const cleanSegment = (value) => {
  const text = String(value || '').trim();
  return text && text !== 'undefined' && text !== 'null' ? text : '';
};

export const getCatalogContextFromUrl = (value) => {
  if (!value) return {};
  try {
    const url = new URL(value, getDefaultOrigin());
    const marketerMatch = url.pathname.match(/^\/p\/([^/]+)\/catalog\/([^/]+)\/?$/i);
    if (marketerMatch) {
      return {
        username: decodeURIComponent(marketerMatch[1]),
        catalogSlug: decodeURIComponent(marketerMatch[2])
      };
    }

    const legacyMatch = url.pathname.match(/^\/c\/([^/]+)\/?$/i);
    return legacyMatch ? { catalogSlug: decodeURIComponent(legacyMatch[1]) } : {};
  } catch (_) {
    return {};
  }
};

export const buildOfferShareUrl = ({
  username,
  catalogSlug,
  offerId,
  currentUrl,
  origin = getDefaultOrigin()
} = {}) => {
  const safeOfferId = cleanSegment(offerId);
  if (!safeOfferId) return '';

  // The current catalog route can fill missing context without copying its filters.
  const routeContext = getCatalogContextFromUrl(currentUrl);
  const safeUsername = cleanSegment(username || routeContext.username);
  const safeCatalogSlug = cleanSegment(catalogSlug || routeContext.catalogSlug);

  let pathname;
  if (safeUsername && safeCatalogSlug) {
    pathname = `/p/${encodeURIComponent(safeUsername)}/catalog/${encodeURIComponent(safeCatalogSlug)}`;
  } else if (safeCatalogSlug) {
    pathname = `/c/${encodeURIComponent(safeCatalogSlug)}`;
  } else {
    // A direct public-offer route is safer than producing /c/undefined.
    pathname = `/offer/${encodeURIComponent(safeOfferId)}`;
  }

  const url = new URL(pathname, origin);
  if (safeCatalogSlug) url.searchParams.set('offer', safeOfferId);
  return url.toString();
};

const isOfferShareUrl = (candidate) => {
  try {
    const url = new URL(candidate, getDefaultOrigin());
    const isCatalogOffer = (
      /^\/c\/[^/]+\/?$/i.test(url.pathname) ||
      /^\/p\/[^/]+\/catalog\/[^/]+\/?$/i.test(url.pathname)
    ) && Boolean(url.searchParams.get('offer'));
    return isCatalogOffer || /^\/offer\/[^/]+\/?$/i.test(url.pathname);
  } catch (_) {
    return false;
  }
};

export const stripTrailingMiskOfferLinks = (value) => {
  let text = String(value || '').trimEnd();
  // Remove only offer-share URLs at the end; external links inside descriptions remain untouched.
  const trailingUrl = /(\s*)(https?:\/\/[^\s]+|\/(?:c\/[^\s]+|p\/[^\s]+\/catalog\/[^\s]+|offer\/[^\s]+))\s*$/i;

  while (text) {
    const match = text.match(trailingUrl);
    if (!match || !isOfferShareUrl(match[2])) break;
    text = text.slice(0, match.index).trimEnd();
  }

  // Remove an orphan label left after stripping its old URL.
  return text.replace(/(?:\r?\n|^)\s*(?:رابط العرض|رابط العقار)\s*:?[\s]*$/i, '').trim();
};

export const buildOfferShareText = ({ text, url, includeUrl = true } = {}) => {
  const cleanText = stripTrailingMiskOfferLinks(text);
  const safeUrl = cleanSegment(url);
  if (!includeUrl || !safeUrl) return cleanText;
  return cleanText ? `${cleanText}\n\n${safeUrl}` : safeUrl;
};

